import React, { Fragment, useEffect, useState } from 'react';
import { UserAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { FormHelperText, FormTextInput } from 'components/form-fields';
import { ExpandableListingTable, ListingTable } from 'components/listing';
import { Button } from 'components/buttons';
import { Tooltip } from 'components/tooltip';
import "./AssetTypes.css";
import { AlertConfirm, NotificationToaster } from 'components/notifications';
import { AssetTypeSubTypesModal } from 'pages/fleet/fleet-settings/asset-types/AssetTypeSubTypesModal';
import { AssetTypesService } from 'services/AssetTypesService';
import { useDebounce } from 'hooks/useDebounce';

export function AssetTypes(props) {
    const requiredActions = ["Fleet:ChangeAssetType"];

    const debounceTimeout = 750;
    const [assetTypeSearchTerm, setAssetTypeSearchTerm] = useState('');
    const [assetTypes, setAssetTypes] = useState([]);
    const [loadingAssetTypes, setLoadingAssetTypes] = useState(false);
    const [subTypeModalTitle, setSubTypeModalTitle] = useState("");
    const [subTypeModalIsUpdate, setSubTypeModalIsUpdate] = useState(false);
    const [showSubTypeModal, setShowSubTypeModal] = useState(false);
    const [showDeleteSubTypeConfirm, setShowDeleteSubTypeConfirm] = useState(false);
    const [selectedAssetType, setSelectedAssetType] = useState({});
    const [selectedAssetSubType, setSelectedAssetSubType] = useState({});
    const [selectedAssetSubTypeIndex, setSelectedAssetSubTypeIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const debouncedAssetTypeSearchTerm = useDebounce(assetTypeSearchTerm, debounceTimeout);
    const columnFill = "-webkit-fill-available";
    const columnButtonWidth = "100px";
    const columnWidths = [null, null, columnButtonWidth];
    const columnHeaders = [
        "Asset Type",
        "Subtypes",
        "Actions"
    ];

    const nestedColumnWidths = [columnFill, columnButtonWidth];
    const nestedColumnHeaders = [
        "Asset Subtype",
        "Actions"
    ];
    
    useEffect(() => {
        loadAssetTypes();
    }, []);

    function loadAssetTypes() {
        setLoadingAssetTypes(true);

        AssetTypesService.getAssetTypesAndSubTypesForAccount()
            .then((data) => {
                setAssetTypes(data);
                setLoadingAssetTypes(false);
            })
            .catch(() => {
                setLoadingAssetTypes(false);
                NotificationToaster.show("Could not load asset types. Please refresh the page.", "danger");
            });
    }

    function onSearchChange(item) {
        setAssetTypeSearchTerm(item.target.value);
    }


    function onChangeVisibilityClick(assetTypeId, event) {
        event.stopPropagation();

        setAssetTypes((prev) => {
            var clonedAssetTypes = [...prev];
            var matchedAssetTypeIndex = clonedAssetTypes.findIndex(l => l.id === assetTypeId);

            if (matchedAssetTypeIndex === -1) { 
                return clonedAssetTypes;
            }   

            clonedAssetTypes[matchedAssetTypeIndex].excluded = !clonedAssetTypes[matchedAssetTypeIndex].excluded;

            updateAssetTypeExclusions(clonedAssetTypes);

            return clonedAssetTypes;
        });
    }

    function updateAssetTypeExclusions(clonedAssetTypes) {
        setSaving(true);

        let model = clonedAssetTypes.filter(x => x.excluded).map((assetType) => {
            return assetType.id;
        });

        AssetTypesService.excludeAssetTypes(model)
            .then(() => {
                setAssetTypes(clonedAssetTypes);
                NotificationToaster.show("success", "Asset type exclusions updated successfully.");
            })
            .catch(() => {
                NotificationToaster.show("danger", "Failed to update asset type exclusions.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onAddSubTypeClick(assetType, event) {
        event.stopPropagation();
        setSelectedAssetType(assetType);
        setSubTypeModalTitle("Add New Subtype");
        setSubTypeModalIsUpdate(false);
        setShowSubTypeModal(true);
    }

    function onEditAssetTypeSubType(assetType, subType, index, event) {
        event.stopPropagation();
        setSelectedAssetType(assetType);
        setSelectedAssetSubType(subType);
        setSelectedAssetSubTypeIndex(index);
        setSubTypeModalTitle("Edit Subtype");
        setSubTypeModalIsUpdate(true);
        setShowSubTypeModal(true);
    }

    function onDeleteSubType(assetType, assetTypeSubTypeIndex, event) {
        event.stopPropagation();
        setSelectedAssetType(assetType);
        setSelectedAssetSubTypeIndex(assetTypeSubTypeIndex);
        setShowDeleteSubTypeConfirm(true);
    }

    function onAssetTypeSubTypeModalSave({ id, name, canDelete, assetTypeId }, action, actionPastTense) {
        setAssetTypes((prev) => {
            var clonedAssetTypes = [...prev];
            var matchedAssetTypeIndex = clonedAssetTypes.findIndex(l => l.id === assetTypeId);

            if (matchedAssetTypeIndex === -1) { 
                return clonedAssetTypes;
            }   

            if (subTypeModalIsUpdate) {
                var matchedSubTypeIndex = clonedAssetTypes[matchedAssetTypeIndex].subtypes.findIndex(l => l.id === id);

                if (matchedSubTypeIndex === -1) { 
                    return clonedAssetTypes;
                }

                clonedAssetTypes[matchedAssetTypeIndex].subtypes[matchedSubTypeIndex].name = name;
            } else {
                if (!clonedAssetTypes[matchedAssetTypeIndex].subtypes) {
                    clonedAssetTypes[matchedAssetTypeIndex].subtypes = [];
                }

                clonedAssetTypes[matchedAssetTypeIndex].subtypes.push({
                    id: id,
                    name: name,
                    canDelete: canDelete
                });
            }

            updateAssetTypeSubTypes(clonedAssetTypes[matchedAssetTypeIndex], action, actionPastTense);

            return clonedAssetTypes;
        });

        onAssetTypeSubTypeModalClose();
    }

    function updateAssetTypeSubTypes(assetType, action, actionPastTense) {
        var subTypes = assetType.subtypes 
            ? assetType.subtypes.map(subType => ({
                id: subType.id,
                name: subType.name
              }))
            : [];

        AssetTypesService.updateAssetTypeSubTypes(assetType.id, subTypes)
            .then(() => {
                NotificationToaster.show("success", `Asset type subtype ${actionPastTense} successfully.`);
            })
            .catch(() => {
                NotificationToaster.show("danger", `Could not ${action} asset type subtype. Please try again.`);
            });
    }

    function onAssetTypeSubTypeModalClose() {
        setShowSubTypeModal(false);
        setSelectedAssetType({});
        setSelectedAssetSubType({});
        setSelectedAssetSubTypeIndex(0);
        setSubTypeModalTitle("");
        setSubTypeModalIsUpdate(false);
    }

    const assetTypeRows = assetTypes.filter(x => x.name.toLowerCase().includes(debouncedAssetTypeSearchTerm.toLowerCase())).map((assetType) => {
        const totalSubTypes = assetType.subtypes ? assetType.subtypes.length : 0;

        const assetTypeRow = [
            <div key={`${assetType.id}-asset-type-name`}>{assetType.name}</div>,
            <div key={`${assetType.id}-asset-type-sub-type-count`}>{totalSubTypes}</div>,
            <div key={`${assetType.id}-asset-type-actions`} className="button-row-small inline-items">
                <Tooltip content={`${assetType.excluded ? "Show" : "Hide"} Asset Type`}>
                    <Button
                        key={`${assetType.id}-visibility-button`}
                        onClick={(e) => onChangeVisibilityClick(assetType.id, e)}
                        icon={assetType.excluded ? "eye-off" : "eye-open"}
                        intent={assetType.excluded ? "none" : "primary"}
                        disabled={loadingAssetTypes || !assetType.canExclude || saving}
                        iconOnly={true}
                        minimal={true}
                        large={false}
                    />
                </Tooltip>
                <Tooltip content="Add Subtypes">
                    <Button
                        key={`${assetType.id}-add-sub-types-button`}
                        onClick={(e) => onAddSubTypeClick(assetType, e)}
                        icon="plus"
                        iconOnly={true}
                        minimal={true}
                        large={false}
                        disabled={saving}
                    />
                </Tooltip>
            </div>
        ];

        return {
            rowContent: assetTypeRow, 
            rowExpandedContent: assetType.subtypes && assetType.subtypes.length > 0 
                ? mapAssetTypeSubTypes(assetType) 
                : null
        };
    });

    function mapAssetTypeSubTypes(assetType) {
        const assetTypeSubTypeData = assetType.subtypes && assetType.subtypes.length > 0 
            ? assetType.subtypes.map((subType, index) => {
                    return [
                        <div key={`${assetType.id}-${subType.id}-asset-type-name`}>{subType.name}</div>,
                        <div key={`${assetType.id}-${subType.id}-edit-sub-type-actions`} className="button-row-small inline-items">
                            <Tooltip content={`Edit Subtype`}>
                                <Button
                                    onClick={(e) => onEditAssetTypeSubType(assetType, subType, index, e)}
                                    icon="edit"
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                    disabled={saving}
                                />
                            </Tooltip>
                            <Tooltip content={`Delete Subtype`}>
                                <Button
                                    onClick={(e) => onDeleteSubType(assetType, index, e)}
                                    intent="delete"
                                    icon="trash"
                                    disabled={!subType.canDelete || saving}
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                />
                            </Tooltip>
                        </div>,
                    ];
                })
            : null;

        return (
            <ListingTable
                key={`${assetType.id}-asset-type-sub-types-table`}
                headers={nestedColumnHeaders}
                pageable={false}
                data={assetTypeSubTypeData}
                loadingData={loadingAssetTypes}
                columnWidths={nestedColumnWidths}
                rowClickEnabled={false}
                totalRecordCount={assetType.subtypes ? assetType.subtypes.length : 0}
            />
        );
    }

    function onDeleteSubTypeConfirm() {
        setAssetTypes((prev) => {
            var clonedAssetTypes = [...prev];

            var matchedAssetTypeIndex = clonedAssetTypes.findIndex(l => l.id === selectedAssetType?.id);

            if (matchedAssetTypeIndex === -1 || !clonedAssetTypes[matchedAssetTypeIndex].subtypes) {
                return clonedAssetTypes;
            }

            clonedAssetTypes[matchedAssetTypeIndex].subtypes.splice(selectedAssetSubTypeIndex, 1);

            updateAssetTypeSubTypes(clonedAssetTypes[matchedAssetTypeIndex], "delete", "deleted");
            return clonedAssetTypes;
        })
    
        onDeleteSubTypeCancel();
    }

    function onDeleteSubTypeCancel() {
        setSelectedAssetType({});
        setSelectedAssetSubTypeIndex(0);
        setShowDeleteSubTypeConfirm(false);
    }

    function getSelectedAssetTypeSubTypeNames() {
        if (!selectedAssetType || !selectedAssetType.subtypes) {
            return [];
        }

        if (subTypeModalIsUpdate) {
            return selectedAssetType.subtypes.filter(x => x.id !== selectedAssetSubType.id).map(subType => subType.name);
        }

        return selectedAssetType.subtypes.map(subType => subType.name);
    }

    return (
        <div className="row">
            <h2>Asset Types</h2>

            <FormHelperText loading={loadingAssetTypes}>
                <p>To hide an asset type at account level please select the eye button. To add subtypes to your asset types please select the plus button on the table rows. </p>
            </FormHelperText>

            <UserAccess perform={requiredActions}
                yes={() => (
                    <Fragment>
                        
                        <FormTextInput 
                            placeholder="Search asset types" 
                            onChange={onSearchChange} 
                            value={assetTypeSearchTerm}
                            large
                            fill
                            icon="search"
                            id="asset-type-search-field"
                            loading={loadingAssetTypes}
                        />


                        <ExpandableListingTable
                            classes="spacer-bottom"
                            loadingData={loadingAssetTypes}
                            rowData={assetTypeRows}
                            headers={columnHeaders}
                            columnWidths={columnWidths}
                            hideClaretIfNullContent={true}
                        />

                        <AssetTypeSubTypesModal
                            showModal={showSubTypeModal}
                            modalTitle={subTypeModalTitle}
                            assetTypeId={selectedAssetType.id}
                            isUpdate={subTypeModalIsUpdate}
                            loading={loadingAssetTypes}
                            onSave={onAssetTypeSubTypeModalSave}
                            onCloseModal={onAssetTypeSubTypeModalClose}
                            assetSubTypeDetails={selectedAssetSubType}
                            assetTypeSubTypeNames={getSelectedAssetTypeSubTypeNames()}
                        />

                        <AlertConfirm
                            title="Delete Subtype"
                            isOpen={showDeleteSubTypeConfirm}
                            onConfirm={onDeleteSubTypeConfirm}
                            onCancel={onDeleteSubTypeCancel}
                        >
                            <p>Are you sure you want to delete this subtype?</p>
                        </AlertConfirm>
                    </Fragment>
                )}
                no={() => (
                    <PageRestricted />
                )}
            />
        </div>
    );
}